export const DOC_CHAT_IMAGE_MIME_TYPES = [
  'image/png',
  'image/jpeg',
  'image/webp',
] as const

export type DocChatImageMimeType = (typeof DOC_CHAT_IMAGE_MIME_TYPES)[number]

export const MAX_DOC_CHAT_ATTACHMENTS = 2

/** Matches the backend's decoded-byte cap in image_ingestion.py. */
export const MAX_DOC_CHAT_ATTACHMENT_BYTES = 5 * 1024 * 1024

/** Downscale target: enough to read a nameplate serial, small enough to post. */
const MAX_EDGE_PX = 2000
const JPEG_QUALITY = 0.85

export interface DocChatPendingAttachment {
  id: string
  name: string
  mimeType: DocChatImageMimeType
  dataBase64: string
  previewUrl: string
}

export function isDocChatImageMimeType(
  mimeType: string
): mimeType is DocChatImageMimeType {
  return (DOC_CHAT_IMAGE_MIME_TYPES as readonly string[]).includes(mimeType)
}

function scaledSize(
  width: number,
  height: number
): { width: number; height: number } {
  const longestEdge = Math.max(width, height)
  if (longestEdge <= MAX_EDGE_PX) return { width, height }
  const scale = MAX_EDGE_PX / longestEdge
  return {
    width: Math.round(width * scale),
    height: Math.round(height * scale),
  }
}

function loadImage(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file)
    const image = new Image()
    image.onload = () => {
      URL.revokeObjectURL(url)
      resolve(image)
    }
    image.onerror = () => {
      URL.revokeObjectURL(url)
      reject(new Error('Could not read that image.'))
    }
    image.src = url
  })
}

function canvasToBlob(
  canvas: HTMLCanvasElement,
  mimeType: string,
  quality: number
): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) resolve(blob)
        else reject(new Error('Could not process that image.'))
      },
      mimeType,
      quality
    )
  })
}

function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => {
      const result = reader.result
      if (typeof result !== 'string') {
        reject(new Error('Could not encode that image.'))
        return
      }
      const separator = result.indexOf(',')
      resolve(separator >= 0 ? result.slice(separator + 1) : result)
    }
    reader.onerror = () => reject(new Error('Could not encode that image.'))
    reader.readAsDataURL(blob)
  })
}

/**
 * Downscale to a size the vision model can still read and the request can
 * still carry, then base64-encode it for the DocChat request body.
 */
export async function prepareImageAttachment(
  file: File
): Promise<DocChatPendingAttachment> {
  if (!isDocChatImageMimeType(file.type)) {
    throw new Error('Attach a PNG, JPEG, or WebP image.')
  }

  const image = await loadImage(file)
  const { width, height } = scaledSize(image.naturalWidth, image.naturalHeight)
  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  const context = canvas.getContext('2d')
  if (!context) throw new Error('Could not process that image.')
  context.drawImage(image, 0, 0, width, height)

  // Re-encode as JPEG unless transparency matters, which only PNG carries.
  const mimeType: DocChatImageMimeType =
    file.type === 'image/png' ? 'image/png' : 'image/jpeg'
  const blob = await canvasToBlob(canvas, mimeType, JPEG_QUALITY)
  if (blob.size > MAX_DOC_CHAT_ATTACHMENT_BYTES) {
    throw new Error('That image is too large even after resizing.')
  }
  const dataBase64 = await blobToBase64(blob)

  return {
    id: crypto.randomUUID(),
    name: file.name || 'attachment',
    mimeType,
    dataBase64,
    previewUrl: `data:${mimeType};base64,${dataBase64}`,
  }
}

export function imageFilesFromDataTransfer(
  items: DataTransferItemList
): File[] {
  const files: File[] = []
  for (const item of items) {
    if (item.kind !== 'file') continue
    const file = item.getAsFile()
    if (file && isDocChatImageMimeType(file.type)) files.push(file)
  }
  return files
}
